import { getLogger } from '../log/logger';
const logger = getLogger();
export function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
export async function retryAsync(operation, options) {
    const { maxRetries, retryDelay, backoffMultiplier = 1, shouldRetry, onRetry, signal, logContext = {}, } = options;
    let lastError;
    let currentDelay = retryDelay;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (signal?.aborted) {
            throw new Error('Operation cancelled');
        }
        try {
            const result = await operation();
            if (shouldRetry && shouldRetry(result, attempt)) {
                if (attempt < maxRetries) {
                    throw new Error('Retry condition requested');
                }
            }
            if (attempt > 0) {
                logger?.debug('Operation succeeded after retry', {
                    attempt,
                    totalAttempts: attempt + 1,
                    ...logContext,
                });
            }
            return result;
        }
        catch (error) {
            lastError = error;
            if (attempt >= maxRetries) {
                logger?.warn('Operation failed after all retries', {
                    maxRetries,
                    error: lastError.message,
                    ...logContext,
                });
                throw lastError;
            }
            logger?.info('Operation failed, retrying...', {
                attempt: attempt + 1,
                maxRetries,
                retryDelay: currentDelay,
                error: lastError.message,
                ...logContext,
            });
            if (onRetry) {
                onRetry(attempt + 1, maxRetries);
            }
            await delay(currentDelay);
            currentDelay = Math.floor(currentDelay * backoffMultiplier);
        }
    }
    throw lastError || new Error('Retry failed');
}
export async function retryUntilCondition(operation, condition, options) {
    const { maxRetries, retryDelay, backoffMultiplier = 1, onRetry, signal, logContext = {}, } = options;
    let currentDelay = retryDelay;
    let result;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        if (signal?.aborted) {
            return {
                success: false,
                result,
                attempts: attempt,
            };
        }
        try {
            result = await operation();
            if (condition(result)) {
                if (attempt > 0) {
                    logger?.debug('Condition met after retries', {
                        attempt,
                        totalAttempts: attempt + 1,
                        ...logContext,
                    });
                }
                return {
                    success: true,
                    result,
                    attempts: attempt + 1,
                };
            }
            if (attempt < maxRetries) {
                logger?.info('Condition not met, retrying...', {
                    attempt: attempt + 1,
                    maxRetries,
                    retryDelay: currentDelay,
                    ...logContext,
                });
                if (onRetry) {
                    onRetry(attempt + 1, maxRetries);
                }
                await delay(currentDelay);
                currentDelay = Math.floor(currentDelay * backoffMultiplier);
            }
        }
        catch (error) {
            logger?.error('Error during retry operation', error, {
                attempt: attempt + 1,
                maxRetries,
                ...logContext,
            });
            if (attempt < maxRetries) {
                await delay(currentDelay);
                currentDelay = Math.floor(currentDelay * backoffMultiplier);
            }
        }
    }
    logger?.warn('Condition not met after all retries', {
        maxRetries,
        ...logContext,
    });
    return {
        success: false,
        result,
        attempts: maxRetries + 1,
    };
}
export function createRetryWrapper(defaultOptions) {
    return {
        async retryAsync(operation, overrideOptions) {
            const options = {
                maxRetries: 3,
                retryDelay: 1000,
                ...defaultOptions,
                ...overrideOptions,
            };
            return retryAsync(operation, options);
        },
        async retryUntilCondition(operation, condition, overrideOptions) {
            const options = {
                maxRetries: 3,
                retryDelay: 1000,
                ...defaultOptions,
                ...overrideOptions,
            };
            return retryUntilCondition(operation, condition, options);
        },
    };
}
//# sourceMappingURL=retry-utils.js.map